import React, { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faCrown,
    faUserTie,
    faUser,
    faUserCheck,
    faClipboardCheck,
    faCheck,
    faBuilding
} from '@fortawesome/free-solid-svg-icons';
import '../Assets/css/RoleTemplates.scss';

// Role template definitions
const roleTemplates = {
    admin: {
        name: 'Admin',
        icon: faCrown,
        color: '#dc3545',
        description: 'Full access to all modules and features',
        permissions: {
            dashboard: { read: true, write: true },
            sales_and_orders: { read: true, write: true },
            business_account: { read: true, write: true },
            user_and_customer: { read: true, write: true },
            supplier: { read: true, write: true },
            tax_and_discount_codes: { read: true, write: true },
            stock: { read: true, write: true },
            bookings_and_calendar: { read: true, write: true },
            reports: { read: true, write: true },
            restaurant: { read: true, write: true },
            notification: { read: true, write: true },
            settings: { read: true, write: true }
        }
    },
    manager: {
        name: 'Manager',
        icon: faUserTie,
        color: '#28a745',
        description: 'Access to most business operations and reports',
        permissions: {
            dashboard: { read: true, write: true },
            sales_and_orders: { read: true, write: true },
            business_account: { read: true, write: true },
            user_and_customer: { read: true, write: false },
            supplier: { read: true, write: true },
            tax_and_discount_codes: { read: true, write: false },
            stock: { read: true, write: true },
            bookings_and_calendar: { read: true, write: true },
            reports: { read: true, write: true },
            restaurant: { read: true, write: true },
            notification: { read: true, write: false },
            settings: { read: true, write: false }
        }
    },
    user: {
        name: 'User',
        icon: faUser,
        color: '#007bff',
        description: 'Basic access to daily operations',
        permissions: {
            dashboard: { read: true, write: false },
            sales_and_orders: { read: true, write: true },
            business_account: { read: true, write: false },
            user_and_customer: { read: true, write: false },
            supplier: { read: true, write: false },
            tax_and_discount_codes: { read: true, write: false },
            stock: { read: true, write: false },
            bookings_and_calendar: { read: true, write: false },
            reports: { read: true, write: false },
            restaurant: { read: true, write: false },
            notification: { read: true, write: false },
            settings: { read: true, write: false }
        }
    },
    staff: {
        name: 'Staff',
        icon: faUserCheck,
        color: '#6c757d',
        description: 'Limited access to specific tasks',
        permissions: {
            dashboard: { read: true, write: false },
            sales_and_orders: { read: true, write: true },
            business_account: { read: true, write: false },
            user_and_customer: { read: true, write: false },
            supplier: { read: true, write: false },
            tax_and_discount_codes: { read: true, write: false },
            stock: { read: true, write: false },
            bookings_and_calendar: { read: true, write: false },
            reports: { read: true, write: false },
            restaurant: { read: true, write: true },
            notification: { read: true, write: false },
            settings: { read: true, write: false }
        }
    },
    cost_center: {
        name: 'Department Manager',
        icon: faBuilding,
        color: '#6c757d',
        description: 'Limited access to specific tasks',
        permissions: {
            dashboard: { read: true, write: false },
            sales_and_orders: { read: true, write: true },
            business_account: { read: true, write: false },
            user_and_customer: { read: true, write: false },
            supplier: { read: true, write: false },
            tax_and_discount_codes: { read: true, write: false },
            stock: { read: true, write: false },
            bookings_and_calendar: { read: true, write: false },
            reports: { read: true, write: false },
            restaurant: { read: true, write: true },
            notification: { read: true, write: false },
            settings: { read: true, write: false }
        }
    }
};

function RoleTemplates({ onRoleDataChange, roleRawData, onRoleNameChange, currentStore }) {
    const isProcurementHub = currentStore?.is_procurement_hub === true || currentStore?.is_procurement_hub === 1;
    const [applyingTemplate, setApplyingTemplate] = useState(null);
    const [successTemplate, setSuccessTemplate] = useState(null);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    // Function to deselect all permissions
    const deselectAllPermissions = () => {
        let updatedRoleData = [...roleRawData];
        
        updatedRoleData = updatedRoleData.map(event => {
            let updatedEvent = {
                ...event,
                crud: false,
                read: false,
                write: false
            };

            if (event.subeventKey && event.subeventKey.length > 0) {
                updatedEvent.subeventKey = event.subeventKey.map(subEvent => ({
                    ...subEvent,
                    crud: false,
                    read: false,
                    write: false,
                    actions: subEvent.actions ? Object.keys(subEvent.actions).reduce((acc, key) => {
                        acc[key] = false;
                        return acc;
                    }, {}) : undefined
                }));
            }

            return updatedEvent;
        });

        // Update the parent component's roleRawData
        if (onRoleDataChange) {
            onRoleDataChange(updatedRoleData);
        }
    };

    // Function to apply role template
    const applyRoleTemplate = (templateType) => {
        const template = roleTemplates[templateType];
        if (!template) return;

        // Check if clicking the same template - toggle deselect
        if (selectedTemplate === templateType) {
            setSelectedTemplate(null);
            deselectAllPermissions();
            
            // Clear role name
            if (onRoleNameChange) {
                onRoleNameChange('');
            }
            
            // Add visual feedback for deselect
            setApplyingTemplate(templateType);
            setTimeout(() => {
                setApplyingTemplate(null);
            }, 300);
            return;
        }

        let updatedRoleData = [...roleRawData];
        
        updatedRoleData = updatedRoleData.map(event => {
            const templatePermission = template.permissions[event.eventKey];
            if (templatePermission) {
                // Calculate crud based on read and write
                const crudValue = templatePermission.read && templatePermission.write;
                
                let updatedEvent = {
                    ...event,
                    crud: crudValue,
                    read: templatePermission.read,
                    write: templatePermission.write
                };

                // Update subeventKey permissions
                if (event.subeventKey && event.subeventKey.length > 0) {
                    updatedEvent.subeventKey = event.subeventKey.map(subEvent => {
                        let updatedSubEvent = {
                            ...subEvent,
                            crud: crudValue,
                            read: templatePermission.read,
                            write: templatePermission.write
                        };

                        // Update actions based on write permission
                        if (subEvent.actions) {
                            updatedSubEvent.actions = { ...subEvent.actions };
                            Object.keys(updatedSubEvent.actions).forEach(actionKey => {
                                if (typeof updatedSubEvent.actions[actionKey] === 'boolean') {
                                    updatedSubEvent.actions[actionKey] = templatePermission.write;
                                }
                            });
                        }

                        return updatedSubEvent;
                    });
                }

                return updatedEvent;
            } else {
                // If no template permission, set all to false
                let updatedEvent = {
                    ...event,
                    crud: false,
                    read: false,
                    write: false
                };

                if (event.subeventKey && event.subeventKey.length > 0) {
                    updatedEvent.subeventKey = event.subeventKey.map(subEvent => ({
                        ...subEvent,
                        crud: false,
                        read: false,
                        write: false,
                        actions: subEvent.actions ? Object.keys(subEvent.actions).reduce((acc, key) => {
                            acc[key] = false;
                            return acc;
                        }, {}) : undefined
                    }));
                }

                return updatedEvent;
            }
        });

        // Update the parent component's roleRawData
        if (onRoleDataChange) {
            onRoleDataChange(updatedRoleData);
        }

        // Set selected template
        setSelectedTemplate(templateType);

        // Update role name
        if (onRoleNameChange) {
            onRoleNameChange(template.name);
        }

        // Add visual feedback
        setApplyingTemplate(templateType);
        setTimeout(() => {
            setApplyingTemplate(null);
            setSuccessTemplate(templateType);
            setTimeout(() => {
                setSuccessTemplate(null);
            }, 500);
        }, 300);
    };

    return (
        <div className="role-templates-section">
            <div className="templates-title">
                <FontAwesomeIcon icon={faClipboardCheck} className="me-2" />
                Quick Role Templates
            </div>
            <div className="templates-container">
                {Object.entries(roleTemplates).filter(([key]) => key !== 'cost_center' || isProcurementHub).map(([key, template]) => (
                    <OverlayTrigger
                        key={key}
                        placement="top"
                        overlay={
                            <Tooltip>
                                <div className="template-tooltip">
                                    <strong>{template.name}</strong>
                                    <p className="mb-1">{template.description}</p>
                                    <small>{selectedTemplate === key ? 'Click to deselect' : 'Click to apply'} this template</small>
                                </div>
                            </Tooltip>
                        }
                    >
                        <button
                            className={`template-icon-btn ${applyingTemplate === key ? 'applying' : ''} ${successTemplate === key ? 'success' : ''} ${selectedTemplate === key ? 'selected' : ''}`}
                            onClick={() => applyRoleTemplate(key)}
                            style={{ 
                                color: template.color,
                                backgroundColor: selectedTemplate === key ? `${template.color}10` : 'white'
                            }}
                            title={`${selectedTemplate === key ? 'Deselect' : 'Apply'} ${template.name} template`}
                        >
                            <FontAwesomeIcon 
                                icon={template.icon} 
                                className="template-icon"
                            />
                            <span className="template-label">{template.name}</span>
                        </button>
                    </OverlayTrigger>
                ))}
            </div>
        </div>
    );
}

export default RoleTemplates;
